import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { FileText, Music, Layers, Link2, Download, Upload, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const ICONS = { pdf: FileText, audio: Music, flashcards: Layers, link: Link2, other: FileText };

export default function ResourcesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = ["admin","receptionist","teacher"].includes(user.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [openUpload, setOpenUpload] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (kindFilter !== "all") params.set("kind", kindFilter);
    if (levelFilter !== "all") params.set("level", levelFilter);
    if (q) params.set("q", q);
    const { data } = await http.get(`/resources?${params}`);
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, [kindFilter, levelFilter]); // eslint-disable-line

  const openResource = async (r) => {
    if (r.external_url) { window.open(r.external_url, "_blank"); return; }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const resp = await fetch(`${base}/api/resources/${r.id}/download`, { headers: { Authorization: `Bearer ${token}` }});
    if (!resp.ok) return toast.error("Download failed");
    const blob = await resp.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const del = async (id) => {
    if (!confirm(t("common.confirmDelete"))) return;
    try { await http.delete(`/resources/${id}`); load(); toast.success(t("common.deleted")); }
    catch { toast.error("Not allowed"); }
  };

  return (
    <PageChrome title={t("resources.title")} subtitle={t("resources.subtitle")} testId="resources-page">
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <Input placeholder={t("common.search")} value={q} onChange={(e)=>setQ(e.target.value)} onBlur={load} className="max-w-xs bg-white" data-testid="res-search"/>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-32 bg-white" data-testid="res-kind-filter"><SelectValue/></SelectTrigger>
          <SelectContent>{["all","pdf","audio","flashcards","link","other"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-32 bg-white" data-testid="res-level-filter"><SelectValue/></SelectTrigger>
          <SelectContent>{["all","A1","A2","B1","B2","C1","C2"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {isStaff && <Button className="ml-auto bg-[#0A1F44]" onClick={()=>setOpenUpload(true)} data-testid="res-upload-btn"><Plus className="h-4 w-4 mr-1"/>{t("resources.add")}</Button>}
      </div>
      <div className="grid grid-cols-3 gap-4" data-testid="res-grid">
        {items.map(r => {
          const Ic = ICONS[r.kind] || FileText;
          return (
            <Card key={r.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`res-card-${r.id}`}>
              <div className="flex items-start gap-3">
                <Ic className="h-8 w-8 text-[#0A1F44] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.title}</div>
                  <div className="text-xs text-slate-500">{r.description}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {r.level && <Badge variant="outline" className="text-xs">{r.level}</Badge>}
                    <Badge variant="outline" className="text-xs">{r.kind}</Badge>
                    {(r.tags||[]).slice(0,3).map(tg => <span key={tg} className="text-xs bg-slate-100 px-2 py-0.5 rounded">{tg}</span>)}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 justify-end mt-3">
                <Button size="sm" variant="ghost" onClick={()=>openResource(r)} data-testid={`res-open-${r.id}`}><Download className="h-4 w-4 mr-1"/>{r.external_url ? t("resources.open") : t("resources.download")}</Button>
                {(user.role === "admin" || r.uploaded_by === user.id) && <Button size="sm" variant="ghost" onClick={()=>del(r.id)}><Trash2 className="h-4 w-4 text-rose-600"/></Button>}
              </div>
            </Card>
          );
        })}
        {items.length === 0 && <div className="col-span-3 text-center text-slate-400 py-10">{t("resources.empty")}</div>}
      </div>
      <UploadModal open={openUpload} onClose={()=>{ setOpenUpload(false); load(); }} t={t}/>
    </PageChrome>
  );
}

function UploadModal({ open, onClose, t }) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("pdf");
  const [level, setLevel] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("everyone");
  const [externalUrl, setExternalUrl] = useState("");
  const [file, setFile] = useState(null);
  const submit = async () => {
    const fd = new FormData();
    fd.append("title", title); fd.append("kind", kind); fd.append("level", level);
    fd.append("tags", tags); fd.append("visibility", visibility);
    if (externalUrl) fd.append("external_url", externalUrl);
    if (file) fd.append("file", file);
    try {
      await http.post("/resources", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(t("resources.uploaded")); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("resources.add")}</DialogTitle></DialogHeader>
        <Input placeholder={t("resources.titleLabel")} value={title} onChange={(e)=>setTitle(e.target.value)} data-testid="res-up-title"/>
        <div className="grid grid-cols-3 gap-2">
          <Select value={kind} onValueChange={setKind}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["pdf","audio","flashcards","link","other"].map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select>
          <Select value={level} onValueChange={setLevel}><SelectTrigger><SelectValue placeholder="level"/></SelectTrigger><SelectContent>{["","A1","A2","B1","B2","C1","C2"].map(l => <SelectItem key={l||"none"} value={l}>{l||"—"}</SelectItem>)}</SelectContent></Select>
          <Select value={visibility} onValueChange={setVisibility}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["everyone","staff","teachers","parents","students"].map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
        </div>
        <Input placeholder={t("resources.tagsCsv")} value={tags} onChange={(e)=>setTags(e.target.value)}/>
        <Input placeholder={t("resources.externalUrl")} value={externalUrl} onChange={(e)=>setExternalUrl(e.target.value)}/>
        <input type="file" onChange={(e)=>setFile(e.target.files[0])} data-testid="res-up-file"/>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={!title} className="bg-[#0A1F44]" data-testid="res-up-submit"><Upload className="h-4 w-4 mr-1"/>{t("resources.upload")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
